import React, { useCallback, useRef } from 'react';
import { AddOperandButton, Filter, getRootOperator, isIngressExp, OperatorType } from '@fabric-ui/components';
import { emptyObject } from '@fabric-ui/utils';
import { Box } from '@mui/material';
import { isNullOrUndefined } from '@utils/utils';
import { useExpressionBuilder } from './ExpressionBuilderContext';
import { shouldExpandFilterGroup } from './expressionOperations';
import { FieldFilterBoxBase } from './FieldFilterBoxBase';
import { useFilterConfig } from './FilterContext';
import { useExpressionOperations } from './hooks/useExpressionOperations';
import { FieldOption, LogicalOperatorType } from './types';

export type { FieldOption };

interface RenderOperatorProps {
  filter: Filter;
  setFilter?: (filter: Filter) => void;
  rootOperator: LogicalOperatorType;
  level?: number;
  path?: string;
  expandedGroupPaths?: Set<string>;
  onExpandGroup?: (path: string, anchor: Filter) => void;
  onToggleLogicalOperators: () => void;
}

const EMPTY_GROUP_PATHS: Set<string> = new Set();
const NOOP_EXPAND_GROUP = () => {};

const operatorColors = {
  and: 'var(--color-surface-interactive-primary-default)',
  or: 'var(--color-border-status-warning-secondary)',
};

interface RenderOperatorItemProps {
  addInline: (index: number) => void;
  deleteAt: (index: number) => void;
  fieldOnly: boolean;
  hasListTab: boolean;
  icon: React.ReactNode;
  index: number;
  path: string;
  expandedGroupPaths: Set<string>;
  innerFilter: Filter;
  isLastOperand: boolean;
  isOnlyOperand: boolean;
  level: number;
  onExpandGroup: (path: string, anchor: Filter) => void;
  onToggleLogicalOperators: () => void;
  replaceAt: (index: number, value: Filter) => void;
  rootOperator: LogicalOperatorType;
  shouldShowIcon: ((filter: Filter) => boolean) | undefined;
}

const RenderOperatorItem: React.FC<RenderOperatorItemProps> = React.memo(
  ({
    addInline,
    deleteAt,
    fieldOnly,
    hasListTab,
    icon,
    index,
    path,
    expandedGroupPaths,
    innerFilter,
    isLastOperand,
    isOnlyOperand,
    level,
    onExpandGroup,
    onToggleLogicalOperators,
    replaceAt,
    rootOperator,
    shouldShowIcon,
  }) => {
    const { disabled, isVertical, size } = useFilterConfig();
    const setInnerFilter = useCallback((value: Filter) => replaceAt(index, value), [replaceAt, index]);
    const onDelete = useCallback(() => {
      deleteAt(index);
    }, [deleteAt, index]);
    const addLogicalExpression = useCallback(() => {
      const expandedPath = level === 0 ? path : path.slice(0, path.lastIndexOf('.'));
      onExpandGroup(expandedPath, innerFilter);
      addInline(index);
    }, [addInline, index, innerFilter, level, onExpandGroup, path]);
    const isIngressExpression = isIngressExp(innerFilter);
    const innerOperator = getRootOperator(innerFilter);
    const isExpandedGroup = expandedGroupPaths.has(path) && (innerOperator === OperatorType.AND || innerOperator === OperatorType.OR);

    return (
      <Box sx={{ borderLeft: `2px solid ${operatorColors[rootOperator]}`, ml: level * 2 }}>
        {!isLastOperand && (
          <Box className={isVertical ? '-my-1 ml-[105px] flex items-start' : '-my-1 ml-[52.5px] flex items-start'}>
            <AddOperandButton
              style={{ translate: '-50%' }}
              size={size}
              operator={rootOperator}
              onClick={onToggleLogicalOperators}
              disabled={disabled}
            />
          </Box>
        )}
        {!isExpandedGroup && !shouldExpandFilterGroup(innerFilter, hasListTab) ? (
          <Box className="flex items-center justify-start gap-2">
            <FieldFilterBoxBase
              rootOperator={rootOperator as OperatorType}
              filter={innerFilter}
              setFilter={setInnerFilter}
              level={level}
              onDelete={onDelete}
              addLogicalExpression={fieldOnly || isOnlyOperand ? undefined : addLogicalExpression}
              isIngressExp={isIngressExpression}
            />
            {(isNullOrUndefined(shouldShowIcon) || shouldShowIcon?.(innerFilter)) && icon}
          </Box>
        ) : (
          <RenderOperator
            filter={innerFilter}
            setFilter={setInnerFilter}
            rootOperator={getRootOperator(innerFilter) as LogicalOperatorType}
            level={level + 1}
            path={path}
            expandedGroupPaths={expandedGroupPaths}
            onExpandGroup={onExpandGroup}
            onToggleLogicalOperators={onToggleLogicalOperators}
          />
        )}
      </Box>
    );
  }
);

const RenderOperator: React.FC<RenderOperatorProps> = ({
  filter,
  setFilter,
  rootOperator,
  level = 0,
  path = '',
  expandedGroupPaths = EMPTY_GROUP_PATHS,
  onExpandGroup = NOOP_EXPAND_GROUP,
  onToggleLogicalOperators,
}) => {
  const {
    defaultField,
    supportParser,
    canDeleteLast,
    relationsProps = emptyObject,
    fieldOnly = false,
    hasListTab = false,
    shouldShowIcon,
    icon,
  } = useExpressionBuilder();
  // useExpressionOperations may compute an "undefined" next filter for some tree mutations (e.g.
  // deleting the last operand when `canDeleteLast` is set). The public `setFilter` prop matches
  // every real consumer's signature `(filter: Filter) => void`, but some of those consumers do
  // rely on receiving `undefined` here to reset their own state (see EditAssetAgingCondition),
  // so this must stay a pass-through and not a runtime guard.
  const { addInline, deleteAt, replaceAt } = useExpressionOperations(filter, setFilter as (filter?: Filter) => void, {
    canDeleteLast,
    defaultEmptyFilter: relationsProps.defaultEmptyFilter,
    defaultField,
    hasListTab,
    level,
    rootOperator,
    supportParser,
  });

  const filterRef = useRef(filter);
  filterRef.current = filter;
  const expandedGroupPathsRef = useRef(expandedGroupPaths);
  expandedGroupPathsRef.current = expandedGroupPaths;
  const deleteAtKeepingExpansion = useCallback(
    (index: number) => {
      const remaining = (filterRef.current[rootOperator]?.operands ?? []).filter((_, operandIndex) => operandIndex !== index);
      if (path && remaining.length > 1 && expandedGroupPathsRef.current.has(path)) {
        onExpandGroup(path, remaining[0]);
      }
      deleteAt(index);
    },
    [deleteAt, onExpandGroup, path, rootOperator]
  );

  const operandsList = filter[rootOperator]?.operands;
  return operandsList?.map((innerFilter, index) => {
    const itemPath = path ? `${path}.${index}` : `${index}`;
    return (
      <RenderOperatorItem
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        addInline={addInline}
        deleteAt={deleteAtKeepingExpansion}
        fieldOnly={fieldOnly}
        hasListTab={hasListTab}
        icon={icon}
        index={index}
        path={itemPath}
        expandedGroupPaths={expandedGroupPaths}
        innerFilter={innerFilter}
        isLastOperand={!(index > 0 && operandsList.length > index)}
        isOnlyOperand={operandsList.length === 1}
        level={level}
        onExpandGroup={onExpandGroup}
        onToggleLogicalOperators={onToggleLogicalOperators}
        replaceAt={replaceAt}
        rootOperator={rootOperator}
        shouldShowIcon={shouldShowIcon}
      />
    );
  });
};

export default RenderOperator;
